
import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Edit2,
  Trash2,
  Save,
  Target,
  Search,
  Filter,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff
} from 'lucide-react';
import { authenticatedFetch } from '../services/auth';
import { API_BASE } from '../utils/apiConfig';

const ObjectiveManagement = ({ onClose }) => {
  const [objectives, setObjectives] = useState([]);
  const [perspectives, setPerspectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [formData, setFormData] = useState({
    perspective_id: '',
    objective_id: '',
    name: '',
    description: '',
    weight: 1,
    created_by: 'admin'
  });

  useEffect(() => {
    fetchObjectives();
    fetchPerspectives();
  }, []);

  const fetchObjectives = async () => {
    try {
      setLoading(true);
      console.log('Buscando objetivos...');

      const response = await authenticatedFetch(`${API_BASE}/api/bsc-objectives`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Objetivos carregados:', data);
      setObjectives(data || []);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar objetivos:', err);
      setError('Erro ao carregar objetivos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPerspectives = async () => {
    try {
      console.log('Buscando perspectivas...');

      const response = await authenticatedFetch(`${API_BASE}/api/perspectives`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Perspectivas carregadas:', data);
      setPerspectives(data || []);
    } catch (err) {
      console.error('Erro ao carregar perspectivas:', err);
      setError('Erro ao carregar perspectivas: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingObjective
        ? `${API_BASE}/api/bsc-objectives/${editingObjective.id}`
        : `${API_BASE}/api/bsc-objectives`;

      const method = editingObjective ? 'PUT' : 'POST';

      const payload = editingObjective
        ? {
          name: formData.name,
          description: formData.description,
          is_active: formData.is_active !== false,
          weight: parseFloat(formData.weight) || 0
        }
        : { ...formData, weight: parseFloat(formData.weight) || 0 };

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      await fetchObjectives();
      setShowModal(false);
      setEditingObjective(null);
      setFormData({
        perspective_id: '',
        objective_id: '',
        name: '',
        description: '',
        weight: 1,
        created_by: 'admin'
      });
    } catch (err) {
      console.error('Erro ao salvar objetivo:', err);
      setError('Erro ao salvar objetivo: ' + err.message);
    }
  };

  const handleEdit = (objective) => {
    setEditingObjective(objective);
    setFormData({
      perspective_id: objective.perspective_id,
      objective_id: objective.objective_id,
      name: objective.name,
      description: objective.description,
      is_active: objective.is_active,
      weight: objective.weight ?? 1,
      created_by: objective.created_by
    });
    setShowModal(true);
  };

  const handleDelete = async (objectiveId) => {
    if (!window.confirm('Tem certeza que deseja desativar este objetivo?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_BASE}/api/bsc-objectives/${objectiveId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      await fetchObjectives();
    } catch (err) {
      console.error('Erro ao deletar objetivo:', err);
      setError('Erro ao deletar objetivo: ' + err.message);
    }
  };

  const filteredObjectives = objectives.filter(obj => {
    const matchesSearch = obj.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      obj.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      obj.objective_id.toString().includes(searchTerm.toLowerCase());

    const matchesFilter = filterActive === 'all' ||
      (filterActive === 'active' && obj.is_active) ||
      (filterActive === 'inactive' && !obj.is_active);

    return matchesSearch && matchesFilter;
  });

  const getPerspectiveName = (perspectiveId) => {
    const perspective = perspectives.find(p => p.perspective_id === perspectiveId);
    return perspective ? perspective.name : `ID: ${perspectiveId}`;
  };

  return (

    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="h-full">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="px-4 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Gestão de Objetivos</h1>
                  <p className="text-gray-600 text-sm">Gerenciar objetivos estratégicos das perspectivas BSC</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="w-full mx-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* Filtros */}
          <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar objetivos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterActive('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterActive === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Todos
                </button>
                <button
                  onClick={() => setFilterActive('active')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterActive === 'active' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Ativos
                </button>
                <button
                  onClick={() => setFilterActive('inactive')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterActive === 'inactive' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Inativos
                </button>
                <div className="ml-4 border-l border-gray-300 pl-4">
                  <button
                    onClick={() => {
                      setEditingObjective(null);
                      setFormData({
                        perspective_id: '',
                        objective_id: '',
                        name: '',
                        description: '',
                        weight: 1,
                        created_by: 'admin'
                      });
                      setShowModal(true);
                    }}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-white font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Novo Objetivo
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Objetivos */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando objetivos...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Perspectiva
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Descrição
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Criado por
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredObjectives.map((objective) => (
                      <tr key={objective.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${objective.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {objective.is_active ? (
                              <>
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Ativo
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-3 h-3 mr-1" />
                                Inativo
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getPerspectiveName(objective.perspective_id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {objective.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {objective.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {objective.created_by || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(objective)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(objective.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredObjectives.length === 0 && (
                  <div className="p-12 text-center">
                    <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Nenhum objetivo encontrado</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Modal de Edição/Criação */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingObjective ? 'Editar Objetivo' : 'Novo Objetivo'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingObjective && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Perspectiva
                  </label>
                  <select
                    value={formData.perspective_id}
                    onChange={(e) => setFormData({ ...formData, perspective_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Selecione uma perspectiva</option>
                    {perspectives.map((perspective) => (
                      <option key={perspective.perspective_id} value={perspective.perspective_id}>
                        {perspective.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Peso (% na perspectiva)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Soma dos pesos dos objetivos da perspectiva deve totalizar 100%.
                </p>
              </div>

              {editingObjective && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Ativo
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObjectiveManagement;
